import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import InputData from "./InputData";
import Dropdown from "./Dropdown";
import InputTextArea from "./InputTextArea";
import InputIconSearch from "./InputIconSearch";
import {
  hearingAidsTypeOptions,
  userData,
  hearingAidsStructure,
  selectedOptic,
  focusElement,
  checkOfferActive,
} from "../js/utils";
import { post } from "../js/post";
import TooltipBox from "./Tooltip";
import AdvancedWarehouseProductInfo from "./AdvancedWarehouseProductInfo";
import PurchaseAndSaleData from "./PurchaseAndSaleData";
import moment from "moment";
import { useSuppliers } from "../hooks/useSuppliers";
import { useBrands } from "../hooks/useBrands";
import { useModelsTradeNamesAndProducts } from "../hooks/useModelsTradeNamesAndProducts";

const HearingAidsData = forwardRef((props, ref) => {
  const [currentProduct, setCurrentProduct] = useState(hearingAidsStructure);
  const [currentProductFixed, setCurrentProductFixed] =
    useState(hearingAidsStructure);
  const [OPTCode, setOPTCode] = useState(null);
  const supplierList = useSuppliers(0)[0].map((e) => ({
    id: e.id,
    text: e.nombre,
  }));
  const [rawBrandList, refreshBrands] = useBrands(0, props.supplierCode, "");
  const brandList = useMemo(() => {
    return [
      { id: "", text: "" },
      ...rawBrandList.map((e) => ({
        id: e,
        text: e,
      })),
    ];
  }, [rawBrandList]);

  const [rawModelsList, refreshModels] = useModelsTradeNamesAndProducts(
    [0],
    currentProduct.idProveedor,
    currentProduct.marca !== "" ? [currentProduct.marca] : [],
  );
  const modelsList = useMemo(() => {
    return [
      { id: "", text: "" },
      ...rawModelsList.map((e) => ({
        id: e,
        text: e,
      })),
    ];
  }, [rawModelsList]);
  const [productCode, setProductCode] = useState(props.code);
  const [toggleAdvancedData, setToggleAdvancedData] = useState(false);
  const [activeOffer, setActiveOffer] = useState(false);

  // console.log("currentProduct: ", currentProduct);

  const handleProductChange = (field, value) => {
    setCurrentProduct((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  async function getProductByCode(code, codeType) {
    if (code !== "") {
      const response = await post("ObtenerAudifono.php", {
        token: userData.token,
        codigo: code,
        tipoCodigo: codeType,
        idOptica: selectedOptic.id,
      });
      // console.log(
      // "OBTENIENDO AUDÍFONO: ",
      // {
      // token: userData.token,
      // codigo: code,
      // tipoCodigo: codeType,
      // idOptica: selectedOptic.id,
      // },
      // response,
      // );
      if (Object.keys(response).length === 0) {
        setCurrentProduct({ ...currentProduct, codigoOPTIWIN: OPTCode });
        setCurrentProductFixed({ ...currentProduct, codigoOPTIWIN: OPTCode });
        props.handleProductNotFound(code);
      } else {
        setCurrentProduct(response);
        setCurrentProductFixed(response);
        setOPTCode(response.codigoOPTIWIN);
        props.onSearchByCode(response.codigoOPTIWIN);
      }
      refreshBrands(response.idProveedor);
    }
  }

  function recalculateLabeling(
    marca = currentProduct.marca,
    modelo = currentProduct.modelo,
    tipo = currentProduct.tipo,
  ) {
    let etiquetado = "";

    if (marca) {
      const marcaParts = marca.trim().split(" ");

      if (marcaParts.length === 1) {
        etiquetado += marcaParts[0].slice(0, 3);
      } else {
        marcaParts.forEach((part) => {
          if (part.length > 0) {
            etiquetado += part[0];
          }
        });
      }
    }

    if (modelo) {
      if (etiquetado) etiquetado += " ";
      etiquetado += modelo.slice(0, 5).trimEnd();
    }

    if (tipo) {
      if (etiquetado) etiquetado += " ";
      etiquetado += tipo.slice(0, 3);
    }

    etiquetado = etiquetado.slice(0, 15);

    setCurrentProduct((prev) => ({
      ...prev,
      etiquetado,
    }));
    setCurrentProductFixed((prev) => ({
      ...prev,
      etiquetado,
    }));
  }

  const resetProduct = async (condition) => {
    if (condition > 1) {
      setTimeout(() => {
        focusElement(condition);
      }, 0);
    } else {
      refreshBrands(props.supplierCode);
      refreshModels(
        0,
        currentProduct.idProveedor,
        currentProduct.marca !== "" ? [currentProduct.marca] : [],
      );
      switch (condition) {
        case 0:
          const { marca, idProveedor, nombreProveedor } = currentProduct;
          setCurrentProduct(hearingAidsStructure);
          setCurrentProductFixed(hearingAidsStructure);
          setCurrentProduct((prev) => ({
            ...prev,
            marca: marca,
            idProveedor: idProveedor,
            nombreProveedor: nombreProveedor,
          }));
          setCurrentProductFixed((prev) => ({
            ...prev,
            marca: marca,
            idProveedor: idProveedor,
            nombreProveedor: nombreProveedor,
          }));
          break;
        case 1:
          setCurrentProduct((prev) => ({
            ...prev,
            codigoEANUPC: "",
          }));
          setCurrentProductFixed((prev) => ({
            ...prev,
            codigoEANUPC: "",
          }));
          break;
        default:
          break;
      }
    }
  };

  useImperativeHandle(ref, () => ({
    triggerAction: resetProduct,
  }));

  useEffect(() => {
    focusElement(productCode === -1 ? 502 : 501);
    if (productCode !== -1) {
      getProductByCode(productCode, -1);
    } else {
      setCurrentProduct((prevProduct) => ({
        ...prevProduct,
        idProveedor: props.supplierCode,
        marca: props.brand,
      }));
      setCurrentProductFixed((prevProduct) => ({
        ...prevProduct,
        idProveedor: props.supplierCode,
        marca: props.brand,
      }));
      refreshBrands(props.supplierCode);
    }
  }, []);

  useEffect(() => {
    props.handleProduct(currentProduct, currentProductFixed);
  }, [currentProduct]);

  useEffect(() => {
    setProductCode(props.code);
    if (props.code !== -1) {
      getProductByCode(props.code, -1);
    }
  }, [props.code]);

  useEffect(() => {
    setActiveOffer(
      checkOfferActive(
        currentProduct.fechaOfertaInicio,
        currentProduct.fechaOfertaFin,
      ),
    );
  }, [currentProduct.fechaOfertaInicio, currentProduct.fechaOfertaFin]);

  useEffect(() => {
    refreshModels(
      0,
      currentProduct.idProveedor,
      currentProduct.marca !== "" ? [currentProduct.marca] : [],
    );
  }, [currentProduct.idProveedor, currentProduct.marca]);

  return (
    <div className="opt-table-content grid-5" id="product-content-grid">
      {productCode !== -1 && (
        <>
          <div className="cell cell-label">Código de OPTIWIN</div>
          <div className="cell">
            <InputIconSearch
              tabIndex={501}
              type="type-only-numbers"
              size={0}
              maxLength={10}
              input={productCode}
              onChange={(e) => {
                setProductCode(e);
              }}
              onClear={() => {
                setProductCode("");
              }}
              onEnter={async () => {
                props.handleSearch(async () => {
                  await getProductByCode(productCode, 0);
                });
              }}
            ></InputIconSearch>
          </div>
          <div className="cell"></div>
          <div className="cell cell-label">PVP recomendado</div>
          <div className="cell">
            <InputData
              maxLength={20}
              inactive={true}
              type="type-float2"
              size={0}
              input={currentProduct.pvpRecomendado}
              onChange={() => {}}
            ></InputData>
          </div>
        </>
      )}
      <div className="cell cell-label">Código EAN/UPC</div>
      <div className="cell">
        <InputData
          tabIndex={502}
          maxLength={30}
          type="type-alphanum-ext"
          size={0}
          input={currentProduct.codigoEANUPC}
          onChange={(e) => {
            handleProductChange("codigoEANUPC", e);
          }}
        ></InputData>
      </div>
      <div className="cell"></div>
      {productCode !== -1 && (
        <>
          <div className="cell cell-label">
            PVP Oferta
            {activeOffer && (
              <img
                src="/images/check-orange.svg"
                loading="lazy"
                alt="Icon"
                className="cell-icon"
              ></img>
            )}
          </div>
          <div className="cell">
            <InputData
              inactive={true}
              type="type-float2"
              size={0}
              input={currentProduct.pvpOferta}
              onChange={() => {}}
            ></InputData>
          </div>
        </>
      )}
      {productCode === -1 && (
        <>
          <div className="cell cell-label">
            Fecha límite
            {activeOffer && (
              <img
                src="/images/check-orange.svg"
                loading="lazy"
                alt="Icon"
                className="cell-icon"
              ></img>
            )}
          </div>
          <div className="cell">
            <input
              type="datetime-local"
              max="9999-12-31T23:59"
              step={1}
              className="input-field input-cell"
              value={currentProduct.fechaLimite}
              onChange={(e) => {
                const value = e.target.value;
                handleProductChange(
                  "fechaLimite",
                  value === ""
                    ? ""
                    : moment(value).format("YYYY-MM-DD HH:mm:ss"),
                );
              }}
            ></input>
          </div>
        </>
      )}
      {productCode !== -1 && (
        <>
          <div className="cell cell-label">PVP</div>
          <div className="cell">
            <InputData
              tabIndex={503}
              type="type-float2"
              size={0}
              inactive={true}
              input={currentProduct.pvp}
              onChange={() => {}}
            ></InputData>
          </div>
          <div className="cell"></div>
          <div className="cell cell-label">PVP especial</div>
          <div className="cell">
            <InputData
              inactive={true}
              type="type-float2"
              size={0}
              input={currentProduct.pvpEspecial}
              onChange={() => {}}
            ></InputData>
          </div>
        </>
      )}
      <div className="cell combined-cells-5"></div>
      <div className="cell cell-label">Proveedor</div>
      <div className="cell combined-cells-4">
        <Dropdown
          optionList={
            supplierList.length > 0
              ? [{ id: -1, text: "" }, ...supplierList]
              : supplierList
          }
          size={0}
          disabled={true}
          op={currentProduct.idProveedor}
          onChange={async (e) => {
            handleProductChange("nombreProveedor", e.text);
            handleProductChange("idProveedor", e.id);
          }}
        ></Dropdown>
      </div>
      <div className="cell cell-label">Marca (*)</div>
      <div className="cell combined-cells-4">
        <Dropdown
          tabIndex={504}
          optionList={brandList}
          size={0}
          mandatory={false}
          op={currentProduct.marca}
          onChange={(e) => {
            handleProductChange("marca", e.text);
            recalculateLabeling(
              e.text,
              currentProduct.modelo,
              currentProduct.tipo,
            );
          }}
        ></Dropdown>
      </div>
      <div className="cell cell-label">Modelo (*)</div>
      <div className="cell">
        <Dropdown
          tabIndex={505}
          optionList={modelsList}
          size={0}
          mandatory={false}
          op={currentProduct.modelo}
          onChange={(e) => {
            handleProductChange("modelo", e.text);
            recalculateLabeling(
              currentProduct.marca,
              e.text,
              currentProduct.tipo,
            );
          }}
        ></Dropdown>
      </div>
      <div className="cell"></div>
      <div className="cell cell-label">Entre dB</div>
      <div className="cell">
        <InputData
          tabIndex={508}
          type="type-integer"
          size={0}
          input={currentProduct.entreDB}
          onChange={(e) => {
            handleProductChange("entreDB", e);
          }}
        ></InputData>
      </div>
      <div className="cell cell-label">Tipo</div>
      <div className="cell">
        <Dropdown
          tabIndex={506}
          disabled={false}
          optionList={hearingAidsTypeOptions}
          size={0}
          op={currentProduct.tipo}
          onChange={(e) => {
            handleProductChange("tipo", e.text);
            recalculateLabeling(
              currentProduct.marca,
              currentProduct.modelo,
              e.text,
            );
          }}
        ></Dropdown>
      </div>
      <div className="cell"></div>
      <div className="cell cell-label">Hasta dB</div>
      <div className="cell">
        <InputData
          tabIndex={509}
          type="type-integer"
          size={0}
          input={currentProduct.hastaDB}
          onChange={(e) => {
            handleProductChange("hastaDB", e);
          }}
        ></InputData>
      </div>
      <div className="cell cell-label">Nº serie</div>
      <div className="cell">
        <InputData
          tabIndex={507}
          maxLength={20}
          type="type-alphanum-ext"
          size={0}
          input={currentProduct.numeroSerie}
          onChange={(e) => {
            handleProductChange("numeroSerie", e);
          }}
        ></InputData>
      </div>
      <div className="cell"></div>
      <div className="cell cell-label">Ref. Proveedor</div>
      <div className="cell">
        <InputData
          tabIndex={510}
          maxLength={20}
          type="type-alphanum-ext"
          size={0}
          input={currentProduct.referenciaProveedor}
          onChange={(e) => {
            handleProductChange("referenciaProveedor", e);
          }}
        ></InputData>
      </div>
      <div className="cell cell-label">Etiquetado</div>
      <div className="cell combined-cells-4">
        <InputData
          tabIndex={511}
          maxLength={15}
          type="type-alphanum-ext"
          size={0}
          input={currentProduct.etiquetado}
          onChange={(e) => {
            handleProductChange("etiquetado", e);
          }}
        ></InputData>
        <button className="cell-icon" onClick={() => recalculateLabeling()}>
          <img
            data-tooltip-id="tt1"
            src="/images/update-fill-blue.svg"
            loading="lazy"
            alt="Icon"
            className="cell-icon"
          ></img>
          <TooltipBox
            id="tt1"
            content={
              <div className="cell">Recalcular etiquetado del producto.</div>
            }
          ></TooltipBox>
        </button>
      </div>
      <div className="cell cell-label">Observaciones</div>
      <div className="cell combined-cells-4">
        <InputTextArea
          tabIndex={512}
          maxLength={200}
          type="type-full"
          size={0}
          input={currentProduct.observaciones}
          onChange={(e) => {
            handleProductChange("observaciones", e);
          }}
          extra="small-height"
        ></InputTextArea>
      </div>
      <div className="cell combined-cells-5"></div>
      {productCode !== -1 && (
        <div className="combined-cells-5 opt-table-title">
          Datos avanzados de producto
          <button
            className="cell-icon"
            onClick={() => {
              setToggleAdvancedData(!toggleAdvancedData);
            }}
          >
            <img
              src={
                toggleAdvancedData
                  ? "/images/arrow-up-blue.svg"
                  : "/images/arrow-down-blue.svg"
              }
              loading="lazy"
              alt="Icon"
              className="cell-icon"
            ></img>
          </button>
        </div>
      )}
      {toggleAdvancedData && productCode !== -1 && (
        <AdvancedWarehouseProductInfo
          product={currentProduct}
          handleProductChange={handleProductChange}
        />
      )}
      {productCode === -1 && (
        <div className="cell combined-cells-5 opt-table-title">
          Datos de compra y venta
        </div>
      )}
      {productCode === -1 && (
        <PurchaseAndSaleData
          product={currentProduct}
          handleProductChange={handleProductChange}
          registerFromDeliveryNote={props.registerFromDeliveryNote}
          family={0}
        />
      )}
    </div>
  );
});
export default HearingAidsData;
